import { useAuth0 } from "@auth0/auth0-react";
import { useQuery } from "@tanstack/react-query";
import { Input, Menu, message, Tooltip } from "antd";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { IoWalletOutline } from "react-icons/io5";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  getAccStatus,
  getUserAccounts,
  setAccount,
} from "../../actions/acc_actions";
import { signout } from "../../actions/auth_action";
import Loading from "../../utils/Loading";
import Button from "../Button";

const Sidebar = ({ collapsed }) => {
  const selectedAccountUrn = useSelector((state) => state.account.urn);
  const { isLoggedIn, token } = useSelector((state) => state.auth);
  const [searchTerm, setSearchTerm] = useState("");
  const accUrn = useSelector((state) => state.account.urn);

  const { logout } = useAuth0();
  const { t } = useTranslation();
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { data: accStatusData } = useQuery({
    queryKey: ["accStatus", accUrn],
    queryFn: () => getAccStatus(accUrn),
    enabled: !!accUrn,
  });

  const completeInfo =
    accStatusData?.data?.businessesCount > 0 &&
    accStatusData?.data?.outletsCount > 0 &&
    accStatusData?.data?.rewardsCount > 0 &&
    accStatusData?.data?.stampcardsCount > 0;

  const handleLogout = () => {
    logout();
    dispatch(signout());
  };

  const {
    error,
    isLoading,
    data: accounts,
  } = useQuery({
    queryKey: ["userAccounts"],
    queryFn: () => dispatch(getUserAccounts()),
    enabled: isLoggedIn && !!token,
  });

  const filteredAccounts = useMemo(() => {
    if (!accounts?.accounts) return [];
    return accounts.accounts.filter((account) =>
      account.name.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [accounts, searchTerm]);

  if (isLoading) return <Loading />;
  if (error) return null;

  if (!(accounts?.accounts?.length > 0) && isLoggedIn && !!token) {
    if (location.pathname !== "/no-account") {
      navigate("/no-account", { replace: true });
    }
  }

  const handleAccountClick = (account) => {
    try {
      dispatch(setAccount(account));
    } catch {
      message.error(t("sidebar_something_went_wrong"));
    }
  };
  const truncateText = (text, maxLength = 15) =>
    text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
  const accountItems =
    filteredAccounts
      .map((account) => ({
        key: account.urn,
        label: (
          <Tooltip title={account.name}>
            <Button
              onClick={() => handleAccountClick(account)}
              variant={
                selectedAccountUrn === account.urn ? "primary" : "secondary"
              }
              style={{ height: "100%", width: "100%", fontSize: "12px" }}
            >
              {truncateText(account.name, 18)}
            </Button>{" "}
          </Tooltip>
        ),
      }))
      .slice(0, 20) || [];

  const items = [
    {
      key: "1",
      icon: (
        <img
          style={{ width: 24, height: 24 }}
          src="/assets/images/icons/vector.png"
          alt=""
        />
      ),
      label: (
        <Link disabled={!completeInfo} to="/home">
          {t("sidebar_home")}
        </Link>
      ),
      disabled: !completeInfo,
    },
    {
      key: "2",
      icon: (
        <img
          style={{ width: 24, height: 24 }}
          src="/assets/images/icons/marketing.png
          "
          alt=""
        />
      ),
      label: (
        <Link disabled={!completeInfo} to="/campaigns">
          {t("sidebar_campaigns")}
        </Link>
      ),
      disabled: !completeInfo,
    },
    {
      key: "3",
      icon: (
        <img
          style={{ width: 24, height: 24 }}
          src="/assets/images/icons/magic_code.png"
          alt=""
        />
      ),
      label: (
        <Link disabled={!completeInfo} to="/magic-codes">
          {t("sidebar_magic_codes")}
        </Link>
      ),
      disabled: !completeInfo,
    },
    {
      key: "4",
      icon: (
        <img
          style={{ width: 24, height: 24 }}
          src="/assets/images/icons/insights.png"
          alt=""
        />
      ),
      label: (
        <Link disabled={!completeInfo} to="/insights">
          {t("sidebar_insights")}
        </Link>
      ),
      disabled: !completeInfo,
    },
    {
      key: "9",
      icon: (
        <img
          style={{ width: 24, height: 24 }}
          src="/assets/images/icons/shop.png"
          alt=""
        />
      ),
      label: (
        <Link disabled={!completeInfo} to="/shop">
          {t("sidebar_shop")}
        </Link>
      ),
      disabled: !completeInfo,
    },
    {
      key: "orders",
      icon: (
        <img
          style={{ width: 26, height: 26 }}
          src="/assets/images/icons/orders.png"
          alt=""
        />
      ),
      label: (
        <Link disabled={!completeInfo} to="/merchant">
          {t("sidebar_orders")}
        </Link>
      ),
      disabled: !completeInfo,
    },
    {
      key: "payout",
      icon: <IoWalletOutline size={24} />,
      label: (
        <Link disabled={!completeInfo} to="/payout">
          {t("sidebar_payout")}
        </Link>
      ),
      disabled: !completeInfo,
    },
    { type: "divider" },
    {
      key: "5",
      icon: (
        <img
          style={{ width: 24, height: 24 }}
          src="/assets/images/icons/dashboard.svg"
          alt=""
        />
      ),
      label: <Link to="/overview">{t("sidebar_account_overview")}</Link>,
    },
    {
      key: "6",
      icon: (
        <img
          style={{ width: 24, height: 24 }}
          src="/assets/images/icons/rewards.png"
          alt=""
        />
      ),
      label: <Link to="/rewards">{t("sidebar_rewards")}</Link>,
    },
    {
      key: "7",
      icon: (
        <img
          style={{ width: 24, height: 24 }}
          src="/assets/images/icons/stampcard.png"
          alt=""
        />
      ),
      label: <Link to="/stamp-card">{t("sidebar_stamp_cards")}</Link>,
    },
    {
      key: "8",
      icon: (
        <img
          style={{ width: 24, height: 24 }}
          src="/assets/images/icons/outlets.png"
          alt=""
        />
      ),
      label: <Link to="/outlets">{t("sidebar_outlets")}</Link>,
    },
    { type: "divider" },
    {
      key: "sub-accounts",
      label: t("sidebar_accounts"),
      icon: (
        <img
          style={{ width: 24, height: 24 }}
          src="/assets/images/icons/accounts.png"
          alt=""
        />
      ),
      children: [
        {
          key: "account-search",
          label: (
            <Input
              placeholder={t("sidebar_search_accounts")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: "100%" }}
            />
          ),
          disabled: true,
        },
        ...accountItems,
      ],
    },
    {
      key: "15",
      icon: (
        <img
          style={{ width: 24, height: 24 }}
          src="/assets/images/icons/setting.svg"
          alt=""
        />
      ),
      label: <Link to="/settings">{t("sidebar_settings")}</Link>,
    },
    {
      key: "16",
      icon: (
        <img
          style={{ width: 24, height: 24 }}
          className="my-svg"
          src="/assets/images/icons/logout.png"
          alt=""
        />
      ),
      label: <Link onClick={handleLogout}>{t("sidebar_logout")}</Link>,
    },
  ];

  const selectedKey = items
    .filter(
      (item) =>
        item.label?.props?.to &&
        location.pathname.includes(item.label.props.to),
    )
    .map((item) => item.key);

  return (
    <Menu
      className="sidebar"
      selectedKeys={selectedKey}
      mode="inline"
      inlineCollapsed={collapsed}
      items={items}
    />
  );
};

export default Sidebar;
